import React, { useEffect, useMemo, useState } from 'react';
// Note: we intentionally do NOT use @apidevtools/json-schema-ref-parser here.
// That library depends on Node's `Buffer` global, which CRA does not polyfill
// in the browser, and pulling it in produces "Buffer is not defined" at
// runtime in the panel. Our schema only uses local `#/definitions/X` $refs,
// so a small recursive resolver below is sufficient.

/**
 * BQ-22: Field-by-field schema reference, rendered live from
 * `/data-schema.json` so the in-app docs never drift from the schema we
 * actually validate against. The panel is collapsible and intentionally
 * verbose for first-time users — a "Copy minimal template" button gives
 * power users a clipboard skeleton, and a "Download example file" button
 * grabs `/example-data.json` for a full working sample.
 */

interface SchemaReferencePanelProps {
  defaultExpanded?: boolean;
}

interface FieldRow {
  /** Dotted path from the root, e.g. `examples[].modelResponses[].entities[].start`. */
  path: string;
  type: string;
  required: boolean;
  description: string;
  examplePreview: string | null;
}

// A small skeleton template handed to users via "Copy minimal template".
// Intentionally minimal: one example, one model, one entity, no optional
// niceties — the SchemaReferencePanel itself documents the optional fields.
const MINIMAL_TEMPLATE = {
  schemaVersion: 2,
  examples: [
    {
      id: 'q1',
      text: 'Replace this with the text you want to analyze.',
      modelResponses: [
        {
          modelName: 'Model A',
          entities: [
            { text: 'Replace', label: 'EXAMPLE_LABEL', start: 0, end: 7 },
          ],
        },
      ],
    },
  ],
  modelNames: ['Model A'],
};

function describeType(node: any): string {
  if (!node || typeof node !== 'object') return 'unknown';
  if (Array.isArray(node.type)) return node.type.join(' | ');
  if (node.type === 'array') {
    const itemType = node.items ? describeType(node.items) : 'any';
    return `array<${itemType}>`;
  }
  if (node.type === 'object') return 'object';
  if (typeof node.type === 'string') return node.type;
  if (node.const !== undefined) return `const ${JSON.stringify(node.const)}`;
  return 'unknown';
}

function previewExampleValue(node: any): string | null {
  if (!node || typeof node !== 'object') return null;
  if (node.const !== undefined) return JSON.stringify(node.const);
  if (Array.isArray(node.examples) && node.examples.length > 0) {
    return JSON.stringify(node.examples[0]);
  }
  if (node.type === 'string') return '"example"';
  if (node.type === 'integer') return '0';
  if (node.type === 'number') return '0.5';
  if (node.type === 'boolean') return 'true';
  if (node.type === 'array') return '[]';
  if (node.type === 'object') return '{}';
  return null;
}

/**
 * Walk the dereferenced schema and emit a flat list of field rows. We stop
 * at object boundaries (no nested object recursion past 3 levels) to keep
 * the table readable; the example file covers the rest.
 */
function flattenSchema(schema: any): FieldRow[] {
  const rows: FieldRow[] = [];

  const walk = (node: any, path: string, required: boolean, depth: number) => {
    if (!node || typeof node !== 'object' || depth > 4) return;

    if (node.type === 'array' && node.items) {
      // Record the array property itself and then descend into items with
      // a `[]` suffix so paths read like examples[].modelResponses[].
      // The array node was already pushed by the parent walker.
      walk(node.items, `${path}[]`, false, depth + 1);
      return;
    }

    if (node.type !== 'object' || !node.properties) return;

    const requiredSet = new Set<string>(Array.isArray(node.required) ? node.required : []);
    for (const [key, child] of Object.entries(node.properties as Record<string, any>)) {
      const childPath = path ? `${path}.${key}` : key;
      const isRequired = requiredSet.has(key);
      rows.push({
        path: childPath,
        type: describeType(child),
        required: isRequired,
        description: typeof child?.description === 'string' ? child.description : '',
        examplePreview: previewExampleValue(child),
      });
      if (child?.type === 'array' && child.items) {
        walk(child.items, `${childPath}[]`, false, depth + 1);
      } else if (child?.type === 'object' && child.properties) {
        walk(child, childPath, isRequired, depth + 1);
      }
    }
  };

  walk(schema, '', false, 0);
  return rows;
}

/**
 * Local-ref dereferencer for our schema's `#/definitions/X` pointers.
 * Recurses with a depth cap to break cycles. We don't support remote $refs.
 */
function derefLocalSchema(root: any): any {
  const definitions = (root && typeof root === 'object' && root.definitions) || {};
  const visit = (node: any, depth: number): any => {
    if (depth > 50 || node === null || typeof node !== 'object') return node;
    if (Array.isArray(node)) return node.map((n) => visit(n, depth + 1));
    if (typeof node.$ref === 'string' && node.$ref.startsWith('#/definitions/')) {
      const key = node.$ref.slice('#/definitions/'.length);
      const target = definitions[key];
      if (target == null) return node;
      // Merge: $ref-siblings (rare) override the dereferenced shape.
      const { $ref, ...rest } = node;
      return visit({ ...target, ...rest }, depth + 1);
    }
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(node)) {
      out[k] = visit(v, depth + 1);
    }
    return out;
  };
  return visit(root, 0);
}

const SchemaReferencePanel: React.FC<SchemaReferencePanelProps> = ({ defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const [rows, setRows] = useState<FieldRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch('/data-schema.json');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();
        const deref = derefLocalSchema(raw);
        if (cancelled) return;
        setRows(flattenSchema(deref));
      } catch (e: any) {
        if (cancelled) return;
        setLoadError(e?.message || 'Failed to load schema');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const flashCopied = (key: string) => {
    setCopied(key);
    window.setTimeout(() => setCopied((v) => (v === key ? null : v)), 1500);
  };

  const copyText = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      flashCopied(key);
    } catch {
      // Clipboard write can fail on insecure origins / older Safari.
      // Fall back to a hidden textarea.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        flashCopied(key);
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  const downloadExample = async () => {
    try {
      const res = await fetch('/example-data.json');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'example-data.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SchemaReferencePanel] download failed:', e);
    }
  };

  const minimalTemplateString = useMemo(
    () => JSON.stringify(MINIMAL_TEMPLATE, null, 2),
    []
  );

  return (
    <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg"
        aria-expanded={isExpanded}
        aria-controls="schema-reference-body"
      >
        <span className="flex items-center gap-2">
          <span className="text-base font-semibold text-gray-900">
            JSON file format reference
          </span>
          <span className="text-xs text-gray-500">
            field-by-field, rendered from <code>/data-schema.json</code>
          </span>
        </span>
        <span className="text-gray-400 text-sm">{isExpanded ? '▾' : '▸'}</span>
      </button>

      {isExpanded && (
        <div id="schema-reference-body" className="px-4 pb-4 pt-2 border-t border-gray-100">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => copyText(minimalTemplateString, 'minimal')}
              className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {copied === 'minimal' ? 'Copied ✓' : 'Copy minimal template'}
            </button>
            <button
              type="button"
              onClick={downloadExample}
              className="px-3 py-1.5 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Download example file
            </button>
            <a
              href="/data-schema.json"
              download="data-schema.json"
              className="px-3 py-1.5 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Download schema
            </a>
          </div>

          {loadError && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              Could not load schema: {loadError}
            </div>
          )}

          <details className="mb-2" open>
            <summary className="cursor-pointer text-xs font-medium text-gray-700 hover:text-gray-900">
              Minimal template
            </summary>
            <pre className="mt-2 bg-gray-50 border border-gray-200 rounded p-3 text-[11px] font-mono whitespace-pre-wrap break-words">
{minimalTemplateString}
            </pre>
          </details>

          {!loadError && !rows && (
            <div className="text-xs text-gray-500">Loading schema…</div>
          )}

          {rows && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-3 font-medium">Field</th>
                    <th className="py-2 pr-3 font-medium">Type</th>
                    <th className="py-2 pr-3 font-medium">Required</th>
                    <th className="py-2 pr-3 font-medium">Description</th>
                    <th className="py-2 pr-3 font-medium">Example</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.path} className="border-b border-gray-100 align-top">
                      <td className="py-2 pr-3 font-mono text-[11px] text-gray-800 whitespace-nowrap">
                        {row.path}
                      </td>
                      <td className="py-2 pr-3 font-mono text-[11px] text-gray-700 whitespace-nowrap">
                        {row.type}
                      </td>
                      <td className="py-2 pr-3">
                        {row.required ? (
                          <span className="text-red-700 font-medium">required</span>
                        ) : (
                          <span className="text-gray-500">optional</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-gray-700">{row.description || '—'}</td>
                      <td className="py-2 pr-3">
                        {row.examplePreview ? (
                          <button
                            type="button"
                            onClick={() => copyText(row.examplePreview!, `row-${row.path}`)}
                            className="font-mono text-[11px] text-blue-700 hover:underline"
                            title="Copy example value"
                          >
                            {copied === `row-${row.path}` ? 'Copied ✓' : row.examplePreview}
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default SchemaReferencePanel;
